<script setup>
import { computed, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { getRandomColor } from 'dashboard/helper/labelColor';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();
const store = useStore();

const labels = useMapGetter('labels/getLabels');
const dialogRef = ref(null);
const labelsGuideDialogRef = ref(null);
const newLabelName = ref('');
const isCreatingLabel = ref(false);
const fileInput = ref(null);
const selectedFile = ref(null);
const selectedFileName = ref('');
const selectedLabels = ref([]);
const previewContacts = ref([]);
const previewErrors = ref([]);
const importProgress = ref(0);
const importStatus = ref('idle');
const importMessage = ref('');
const importResult = ref(null);

const isImporting = computed(() => importStatus.value === 'importing');
const canImport = computed(
  () => selectedFile.value && previewContacts.value.length && !isImporting.value
);
const displayedContacts = computed(() => previewContacts.value.slice(0, 100));
const guideCategories = computed(() => [
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.STATUS'),
    examples: ['nuevo', 'en-proceso', 'cerrado'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.SALES_STAGE'),
    examples: ['interesado', 'cotizacion', 'negociacion', 'venta'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.AUTOMATION'),
    examples: ['atendiendo_bot', 'atendiendo_humano'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.PRIORITY'),
    examples: ['urgente', 'vip'],
  },
  {
    title: t('LABEL_MGMT.GUIDE.CATEGORIES.ORIGIN'),
    examples: ['facebook', 'instagram', 'whatsapp', 'web'],
  },
]);

const handleFileClick = () => fileInput.value?.click();

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);
  return baseName.length > 30
    ? `${baseName.slice(0, 30)}...${extension}`
    : fileName;
};

const parseCsvLine = (line, separator) => {
  const values = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const character = line[index];
    if (character === '"' && line[index + 1] === '"' && quoted) {
      value += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === separator && !quoted) {
      values.push(value.trim());
      value = '';
    } else {
      value += character;
    }
  }
  values.push(value.trim());
  return values;
};

const normalizedHeader = header => {
  const mapping = {
    nombre: 'name',
    name: 'name',
    numero: 'phone_number',
    número: 'phone_number',
    telefono: 'phone_number',
    teléfono: 'phone_number',
    phone: 'phone_number',
    phone_number: 'phone_number',
    etiquetas: 'label_list',
    labels: 'label_list',
    label_list: 'label_list',
    tags: 'label_list',
    etiqueta: 'label_list',
  };
  const normalized = header.toLowerCase().trim();
  return mapping[normalized] || normalized;
};

const buildPreview = content => {
  const lines = content
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .filter(line => line.trim());
  if (!lines.length) throw new Error('El archivo CSV está vacío');

  const separator = lines[0].includes(';') ? ';' : ',';
  const headers = parseCsvLine(lines[0], separator).map(normalizedHeader);
  const phoneIndex = headers.indexOf('phone_number');
  const nameIndex = headers.indexOf('name');
  const labelsIndex = headers.indexOf('label_list');
  if (phoneIndex < 0) throw new Error('Falta la columna phone_number o número');

  const contacts = [];
  const errors = [];
  lines.slice(1).forEach((line, index) => {
    const values = parseCsvLine(line, separator);
    const phoneNumber = values[phoneIndex]?.trim();
    const contact = {
      row: index + 2,
      name: nameIndex >= 0 ? values[nameIndex]?.trim() || '' : '',
      phoneNumber: phoneNumber || '',
      labels: labelsIndex >= 0 ? values[labelsIndex]?.trim() || '' : '',
    };
    if (!phoneNumber) errors.push({ ...contact, reason: 'Número vacío' });
    contacts.push(contact);
  });
  previewContacts.value = contacts;
  previewErrors.value = errors;
};

const handleFileChange = async () => {
  const file = fileInput.value?.files[0];
  if (!file) return;

  importResult.value = null;
  importMessage.value = '';
  importProgress.value = 0;
  selectedFile.value = file;
  selectedFileName.value = processFileName(file.name);
  try {
    buildPreview(await file.text());
    importStatus.value = 'preview';
  } catch (error) {
    importStatus.value = 'error';
    importMessage.value = error.message;
  }
};

const toggleLabel = label => {
  selectedLabels.value = selectedLabels.value.includes(label)
    ? selectedLabels.value.filter(item => item !== label)
    : [...selectedLabels.value, label];
};

const createLabel = async () => {
  const title = newLabelName.value.trim().toLowerCase();
  if (!title || isCreatingLabel.value) return;

  const existingLabel = labels.value.find(
    label => label.title.toLowerCase() === title
  );
  if (existingLabel) {
    if (!selectedLabels.value.includes(existingLabel.title)) {
      selectedLabels.value.push(existingLabel.title);
    }
    newLabelName.value = '';
    return;
  }

  isCreatingLabel.value = true;
  try {
    await store.dispatch('labels/create', {
      title,
      description: t(
        'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CREATED_LABEL_DESCRIPTION'
      ),
      color: getRandomColor(),
      show_on_sidebar: true,
    });
    await store.dispatch('labels/get');
    selectedLabels.value.push(title);
    newLabelName.value = '';
    useAlert(t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.LABEL_CREATED'));
  } catch (error) {
    useAlert(error.message || t('LABEL_MGMT.ADD.API.ERROR_MESSAGE'));
  } finally {
    isCreatingLabel.value = false;
  }
};

const uploadFile = () => {
  if (!canImport.value) return;
  importStatus.value = 'importing';
  importProgress.value = 35;
  importMessage.value = t(
    'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.UPLOADING'
  );
  emit('import', {
    file: selectedFile.value,
    selectedLabels: selectedLabels.value,
  });
};

const setResult = result => {
  importResult.value = result;
  importProgress.value = result.import_percentage || 0;
  importStatus.value = 'result';
  importMessage.value = result.message || '';
};

const setError = message => {
  importStatus.value = 'error';
  importProgress.value = 0;
  importMessage.value = message;
};

const resetResult = () => {
  importResult.value = null;
  importMessage.value = '';
  importProgress.value = 0;
};

const resetImport = () => {
  selectedFile.value = null;
  selectedFileName.value = '';
  selectedLabels.value = [];
  newLabelName.value = '';
  previewContacts.value = [];
  previewErrors.value = [];
  importStatus.value = 'idle';
  if (fileInput.value) fileInput.value.value = null;
  resetResult();
};

defineExpose({ dialogRef, resetImport, setResult, setError });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.TITLE')"
    :confirm-button-label="
      importStatus === 'result'
        ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CLOSE')
        : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.IMPORT')
    "
    :is-loading="isImporting"
    :disable-confirm-button="!canImport && importStatus !== 'result'"
    @confirm="importStatus === 'result' ? dialogRef.close() : uploadFile()"
    @close="resetImport"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.DESCRIPTION') }}
      </p>
    </template>

    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-12 truncate">
          {{
            selectedFileName ||
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.LABEL')
          }}
        </span>
        <Button
          :label="
            selectedFile
              ? t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CHANGE')
              : t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CHOOSE_FILE')
          "
          icon="i-lucide-upload"
          color="slate"
          variant="ghost"
          size="sm"
          type="button"
          class="!w-fit"
          :disabled="isImporting"
          @click="handleFileClick"
        />
      </div>

      <template v-if="selectedFile && importStatus !== 'result'">
        <div
          class="p-4 border-2 border-n-brand/40 rounded-xl bg-n-brand/5 shadow-sm"
        >
          <div class="flex items-center justify-between gap-3 mb-3">
            <div class="flex items-center gap-2">
              <span
                class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-brand text-white i-lucide-tags"
              />
              <div>
                <p class="text-sm font-semibold text-n-slate-12">
                  {{
                    t(
                      'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.APPLY_LABELS'
                    )
                  }}
                </p>
                <p class="text-xs text-n-slate-10">
                  {{
                    t(
                      'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.APPLY_LABELS_HELP'
                    )
                  }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <Button
                v-tooltip.top="t('LABEL_MGMT.GUIDE.BUTTON_TOOLTIP')"
                icon="i-lucide-info"
                color="slate"
                variant="ghost"
                size="xs"
                type="button"
                @click="labelsGuideDialogRef?.open()"
              />
              <span
                class="px-2.5 py-1 text-xs font-semibold rounded-full bg-n-brand text-white"
              >
                {{ selectedLabels.length }}
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.SELECTED') }}
              </span>
            </div>
          </div>

          <div class="flex items-center gap-2 mb-3">
            <input
              v-model="newLabelName"
              type="text"
              class="flex-1 h-9 px-3 text-sm border rounded-lg border-n-weak bg-n-slate-1 text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-brand focus:outline-none"
              :placeholder="
                t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.NEW_LABEL_PLACEHOLDER'
                )
              "
              :disabled="isCreatingLabel"
              @keydown.enter.prevent.stop="createLabel"
            />
            <Button
              type="button"
              icon="i-lucide-plus"
              :label="
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.CREATE_LABEL')
              "
              size="sm"
              :is-loading="isCreatingLabel"
              :disabled="!newLabelName.trim()"
              @click="createLabel"
            />
          </div>

          <div class="flex flex-wrap gap-2 max-h-36 overflow-y-auto">
            <button
              v-for="label in labels"
              :key="label.id"
              type="button"
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs"
              :class="
                selectedLabels.includes(label.title)
                  ? 'border-n-brand bg-n-brand/10 text-n-brand'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggleLabel(label.title)"
            >
              <span
                class="w-2 h-2 rounded-full"
                :style="{ backgroundColor: label.color }"
              />
              {{ label.title }}
            </button>
          </div>
        </div>

        <div class="flex items-center justify-between text-sm">
          <span class="font-medium text-n-slate-12">
            {{
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.PREVIEW', {
                count: previewContacts.length,
              })
            }}
          </span>
          <span v-if="previewErrors.length" class="text-n-ruby-9">
            {{
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.WARNINGS', {
                count: previewErrors.length,
              })
            }}
          </span>
        </div>

        <div class="max-h-64 overflow-auto border border-n-weak rounded-lg">
          <table class="w-full text-xs">
            <thead class="sticky top-0 bg-n-slate-3 text-n-slate-11">
              <tr>
                <th class="p-2 text-left">
                  {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.ROW') }}
                </th>
                <th class="p-2 text-left">
                  {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.NAME') }}
                </th>
                <th class="p-2 text-left">
                  {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.PHONE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="contact in displayedContacts"
                :key="contact.row"
                class="border-t border-n-weak"
                :class="{ 'bg-n-ruby-2': !contact.phoneNumber }"
              >
                <td class="p-2">{{ contact.row }}</td>
                <td class="p-2">{{ contact.name || '-' }}</td>
                <td class="p-2">
                  {{
                    contact.phoneNumber ||
                    t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.EMPTY_PHONE')
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-if="previewContacts.length > 100" class="text-xs text-n-slate-10">
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.PREVIEW_LIMIT', {
              count: previewContacts.length,
            })
          }}
        </p>
      </template>

      <div
        v-if="isImporting || importStatus === 'result'"
        class="flex flex-col gap-2"
      >
        <div class="flex justify-between text-sm">
          <span class="text-n-slate-12">{{ importMessage }}</span>
          <span class="font-medium text-n-slate-12">
            {{ importProgress }}{{ t('COMMON.PERCENT') }}
          </span>
        </div>
        <div class="w-full h-2 bg-n-slate-3 rounded-full overflow-hidden">
          <div
            class="h-full transition-all duration-300"
            :class="importResult?.failed_count ? 'bg-n-amber-9' : 'bg-n-teal-9'"
            :style="{ width: `${importProgress}%` }"
          />
        </div>
      </div>

      <div v-if="importResult" class="grid grid-cols-3 gap-3">
        <div class="p-3 rounded-lg bg-n-teal-3 text-center">
          <strong class="block text-lg text-n-teal-11">{{
            importResult.imported_count
          }}</strong>
          <span class="text-xs text-n-teal-11">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.IMPORTED') }}
          </span>
        </div>
        <div class="p-3 rounded-lg bg-n-ruby-3 text-center">
          <strong class="block text-lg text-n-ruby-11">{{
            importResult.failed_count
          }}</strong>
          <span class="text-xs text-n-ruby-11">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.NOT_IMPORTED') }}
          </span>
        </div>
        <div class="p-3 rounded-lg bg-n-blue-3 text-center">
          <strong class="block text-lg text-n-blue-11">{{
            importResult.total_rows
          }}</strong>
          <span class="text-xs text-n-blue-11">
            {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.TOTAL') }}
          </span>
        </div>
      </div>

      <div
        v-if="importResult?.errors?.length"
        class="border border-n-ruby-6 rounded-lg overflow-hidden"
      >
        <p class="px-3 py-2 text-sm font-medium bg-n-ruby-3 text-n-ruby-11">
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.REJECTED_NUMBERS')
          }}
        </p>
        <div class="max-h-44 overflow-y-auto">
          <div
            v-for="error in importResult.errors"
            :key="`${error.row}-${error.phone_number}`"
            class="flex gap-3 px-3 py-2 text-xs border-t border-n-ruby-5"
          >
            <span class="text-n-slate-10">
              {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.ROW') }}
              {{ error.row }}
            </span>
            <strong class="text-n-ruby-11">
              {{
                error.phone_number ||
                t('CONTACTS_LAYOUT.HEADER.ACTIONS.BASIC_IMPORT.NO_PHONE')
              }}
            </strong>
            <span class="text-n-slate-11">{{ error.reason }}</span>
          </div>
        </div>
      </div>

      <p v-if="importStatus === 'error'" class="text-sm text-n-ruby-9">
        {{ importMessage }}
      </p>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="text/csv,.csv"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>

  <Dialog
    ref="labelsGuideDialogRef"
    width="2xl"
    :title="t('LABEL_MGMT.GUIDE.TITLE')"
    :confirm-button-label="t('LABEL_MGMT.GUIDE.CLOSE')"
    :show-cancel-button="false"
    @confirm="labelsGuideDialogRef?.close()"
  >
    <div class="flex flex-col gap-4">
      <div class="flex gap-3 p-4 border rounded-xl border-n-blue-5 bg-n-blue-3">
        <span class="flex-shrink-0 i-lucide-tags size-6 text-n-blue-10" />
        <p class="text-sm leading-6 text-n-blue-12">
          {{ t('LABEL_MGMT.GUIDE.DESCRIPTION') }}
        </p>
      </div>
      <div>
        <h4 class="mb-3 text-sm font-semibold text-n-slate-12">
          {{ t('LABEL_MGMT.GUIDE.RECOMMENDATION') }}
        </h4>
        <div class="grid gap-3 sm:grid-cols-2">
          <div
            v-for="category in guideCategories"
            :key="category.title"
            class="p-3 border rounded-xl border-n-weak bg-n-slate-2"
          >
            <p class="mb-2 text-sm font-medium text-n-slate-12">
              {{ category.title }}
            </p>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="example in category.examples"
                :key="example"
                class="inline-flex px-2 py-1 font-mono text-xs border rounded-md border-n-weak bg-n-slate-1 text-n-slate-11"
              >
                {{ example }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
